import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { F0Button, F0Dialog, F0Text } from '@factorialco/f0-react'
import {
  Input,
  RichTextEditor,
  RichTextEditorHandle,
  Select,
  Stack,
} from '@factorialco/f0-react/dist/experimental'
import { Cross } from '@factorialco/f0-react/icons/app'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  AddTicketAttachmentDocument,
  CreateTicketDocument,
  GenerateDescriptionDraftDocument,
  TicketsConnectionDocument,
} from 'generated/resources/graphql'
import DocumentType from 'modules/core/components/DocumentType'
import { useDirectUpload } from 'modules/core/hooks/useDirectUpload'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import { useActivity } from 'modules/core/lib/activities/activity'
import { ActivityComponent } from 'modules/core/lib/activities/types'
import { encodeActivity } from 'modules/core/lib/activities/utils'
import { acceptedContentTypes } from 'modules/core/lib/fileFormats'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import DropzoneBox from 'modules/documents/components/Documents/DropzoneBox'
import { MAX_ATTACHMENT_BYTES } from 'modules/ticketing/lib/ticketAttachments'

import { type Category, CategorySelector } from 'modules/ticketing/components/CategorySelector'
import {
  getTicketPriorityLabel,
  getTicketPriorityOptions,
} from 'modules/ticketing/lib/ticketPriority'
import { trackOpenCreateTicketDialog, trackCreateTicket } from 'modules/ticketing/lib/tracking'
import { useAiTicketingFeature } from 'modules/ticketing/hooks/useAiTicketingFeature'

type Data = {
  title: string
  description: string
  categoryId: string
  categoryName: string
  priority: string
}

export const CreateTicketActivity: ActivityComponent<Data> = ({
  title: initialTitle,
  description: initialDescription,
  categoryId: initialCategoryId,
  categoryName: initialCategoryName,
  priority: initialPriority,
}) => {
  const { open, closeActivity } = useActivity()

  const { enabled: aiEnabled } = useAiTicketingFeature()
  const navigate = useNavigate()
  const { search } = useLocation()
  const { company } = useCurrentSession()
  const { editorConfig, loading: loadingEditor } = useRichTextEditor()
  const editorRef = useRef<RichTextEditorHandle>(null)

  const [title, setTitle] = useState(initialTitle || '')
  const [description, setDescription] = useState(initialDescription || '')
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    initialCategoryId
      ? {
          id: initialCategoryId,
          name: initialCategoryName || '',
          description: null,
          priorityMode: '', // will be populated when categories load
        }
      : null
  )
  const validPriorities = new Set<string>(getTicketPriorityOptions())
  const [priority, setPriority] = useState(
    validPriorities.has(initialPriority) ? initialPriority : 'medium'
  )
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [pendingFiles, setPendingFiles] = useState<File[]>([])

  const showPrioritySelector = selectedCategory?.priorityMode === 'manual'

  useEffect(() => {
    trackOpenCreateTicketDialog()
  }, [])

  const [performCreate] = useMutation(CreateTicketDocument, {
    extractorFn: (data) => data.ticketingMutations.createTicketingTicket,
  })

  const [performAddAttachment] = useMutation(AddTicketAttachmentDocument, {
    extractorFn: (data) => data.ticketingMutations.createTicketingTicketAttachment,
  })

  const [performGenerateDescription] = useMutation(GenerateDescriptionDraftDocument, {
    extractorFn: (data) => data.ticketingMutations.generateTicketingTicketDescriptionDraft,
  })

  const descriptionEnhanceConfig = useMemo(
    () => ({
      onEnhanceText: async (params: {
        text: string
        selectedIntent?: string
        customIntent?: string
      }) => {
        let userInstructions: string | undefined
        if (params.selectedIntent === 'improve_writing') {
          userInstructions =
            'Improve the writing quality, clarity and flow. Keep the original meaning.'
        } else if (params.selectedIntent === 'fix_grammar') {
          userInstructions = 'Fix spelling and grammar only. Keep the original content intact.'
        } else {
          userInstructions = params.customIntent || undefined
        }

        try {
          const result = await performGenerateDescription({
            variables: {
              text: params.text,
              userInstructions,
            },
          })
          const handled = handleResult(result, () => {})
          if (handled.success && handled.data?.ticketDescriptionDraft?.description) {
            return {
              success: true,
              text: handled.data.ticketDescriptionDraft.description,
            }
          }
        } catch {
          // fall through to error
        }
        return {
          success: false,
          text: params.text,
          error: i18n.t('ticketing.inbox.ai.generation_failed'),
        }
      },
      enhancementOptions: [
        {
          id: 'improve_description',
          label: i18n.t('ticketing.inbox.ai.enhance_description'),
        },
        {
          id: 'improve_writing',
          label: i18n.t('ticketing.inbox.ai.improve_writing'),
        },
        {
          id: 'fix_grammar',
          label: i18n.t('ticketing.inbox.ai.fix_grammar'),
        },
      ],
    }),
    [performGenerateDescription]
  )

  const { upload: directUpload } = useDirectUpload({
    resourceType: 'Ticketing::Ticket',
  })

  const ticketAcceptedFileTypes = useMemo(
    () => acceptedContentTypes(['application/pdf', 'image/jpeg', 'image/png']),
    []
  )

  const handleFileDrop = useCallback((acceptedFiles: File[]) => {
    const validFiles: File[] = []
    for (const file of acceptedFiles) {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        setSubmitError(i18n.t('ticketing.inbox.attachments.file_too_large'))
        continue
      }
      validFiles.push(file)
    }
    if (validFiles.length > 0) {
      setPendingFiles((prev) => [...prev, ...validFiles])
    }
  }, [])

  const handleFileRemove = useCallback((index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleSubmit = useCallback(async () => {
    const trimmedTitle = title.trim()
    if (!trimmedTitle) {
      setSubmitError(i18n.t('ticketing.inbox.create.title_required'))
      return
    }
    if (!selectedCategory) {
      setSubmitError(i18n.t('ticketing.inbox.create.category_required'))
      return
    }
    if (showPrioritySelector && !priority) {
      setSubmitError(i18n.t('ticketing.inbox.create.priority_required'))
      return
    }
    setSubmitError(null)
    setSubmitting(true)

    try {
      const result = await performCreate({
        variables: {
          companyId: company.id,
          title: trimmedTitle,
          description: description.trim() || null,
          categoryId: selectedCategory.id,
          priority: showPrioritySelector ? priority : null,
        },
        refetchQueries: [TicketsConnectionDocument],
      })
      const handled = handleResult(result, () => {})
      if (!handled.success || handled.data?.ticket == null) {
        setSubmitError(i18n.t('ticketing.inbox.create.create_error'))
        return
      }

      const ticketId = handled.data.ticket.id
      trackCreateTicket({
        priority,
        has_description: description.trim().length > 0,
        has_attachments: pendingFiles.length > 0,
        attachments_count: pendingFiles.length,
        origin: 'manual',
      })
      let attachmentFailed = false

      for (const file of pendingFiles) {
        try {
          const uploadResult = await directUpload(file)
          if (uploadResult.type === 'aborted') continue
          const attachResult = await performAddAttachment({
            variables: {
              ticketId,
              signedBlobId: uploadResult.signedId,
            },
          })
          const attachHandled = handleResult(attachResult, () => {})
          if (!attachHandled.success) {
            attachmentFailed = true
          }
        } catch {
          attachmentFailed = true
        }
      }

      if (attachmentFailed) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('ticketing.inbox.create.attachments_partial_error'),
        })
      }

      navigate({
        search,
        hash: encodeActivity('ticketing.viewTicket', { ticketId: String(ticketId) }),
      })
    } finally {
      setSubmitting(false)
    }
  }, [
    title,
    description,
    priority,
    selectedCategory,
    showPrioritySelector,
    pendingFiles,
    performCreate,
    performAddAttachment,
    directUpload,
    company.id,
    navigate,
    search,
  ])

  return (
    <F0Dialog
      isOpen={open}
      module={{ id: 'inbox', label: i18n.t('ticketing.inbox.create.new_ticket') }}
      onClose={closeActivity}
      primaryAction={{
        label: i18n.t('ticketing.inbox.create.create_button'),
        onClick: handleSubmit,
        disabled: !title.trim() || !selectedCategory || submitting,
        loading: submitting,
      }}
    >
      <Stack paddingX="p-4" paddingY="p-4" gap="4" data-testid="ticket-create-form">
        <CategorySelector value={selectedCategory} onSelect={setSelectedCategory} required />
        <Input
          label={i18n.t('ticketing.inbox.columns.title')}
          value={title}
          onChange={(value) => setTitle(value)}
          placeholder={i18n.t('ticketing.inbox.create.title_placeholder')}
          required
        />
        <Stack gap="1">
          <F0Text variant="description" content={i18n.t('ticketing.inbox.columns.description')} />
          {loadingEditor ? (
            <RichTextEditor.Skeleton rows={4} />
          ) : (
            <RichTextEditor
              ref={editorRef}
              {...editorConfig}
              title={i18n.t('ticketing.inbox.columns.description')}
              placeholder={i18n.t('ticketing.inbox.create.description_placeholder')}
              initialEditorState={{ content: initialDescription || '' }}
              enhanceConfig={aiEnabled ? descriptionEnhanceConfig : undefined}
              onChange={(result) => setDescription(result?.value ?? '')}
              disabled={submitting}
            />
          )}
        </Stack>
        <Stack gap="2">
          <F0Text
            variant="description"
            content={i18n.t('ticketing.inbox.attachments.section_title')}
          />
          <DropzoneBox
            onDrop={handleFileDrop}
            message={i18n.t('ticketing.inbox.attachments.drop_here')}
            accept={ticketAcceptedFileTypes}
            iconName="file-upload"
            multiple
            disabled={submitting}
          />
          {pendingFiles.length > 0 && (
            <Stack gap="1">
              {pendingFiles.map((file, index) => (
                <Stack
                  key={`${file.name}-${index}`}
                  alignItems="center"
                  gap="2"
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    padding: '4px 8px',
                    borderRadius: 6,
                    border: '1px solid var(--f0-color-border-default)',
                  }}
                >
                  <Stack
                    alignItems="center"
                    gap="2"
                    style={{ flexDirection: 'row', minWidth: 0, flex: 1 }}
                  >
                    <DocumentType mimetype={file.type} />
                    <F0Text variant="small" ellipsis content={file.name} />
                  </Stack>
                  <F0Button
                    variant="outline"
                    size="sm"
                    hideLabel
                    icon={Cross}
                    onClick={() => handleFileRemove(index)}
                    data-testid={`remove-file-${index}`}
                  />
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
        {showPrioritySelector && (
          <Select<string>
            label={i18n.t('ticketing.inbox.columns.priority')}
            value={priority}
            onChange={(value: string) => setPriority(value)}
            required
            options={getTicketPriorityOptions().map((p) => ({
              value: p,
              label: getTicketPriorityLabel(p),
            }))}
          />
        )}
        {submitError != null && (
          <Stack>
            <F0Text variant="body" content={submitError} color="critical" />
          </Stack>
        )}
      </Stack>
    </F0Dialog>
  )
}
